/**
 * Reusable donut pie chart for showing distribution data.
 * Used for both "Bookings by Source" and "Guest Countries".
 */
import { PIE_COLORS } from '@lib/accommodationConstants';
import React from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { PieChartTooltip } from './ChartTooltips';

export interface PieDataItem {
  name: string;
  value: number;
  pct: number;
}

interface Props {
  title: string;
  data: PieDataItem[];
  isLoading: boolean;
}

export const DistributionPieChart: React.FC<Props> = ({
  title,
  data,
  isLoading,
}) => (
  <div className="rounded-2xl border border-gray-200 bg-white p-5">
    <h4 className="mb-4 text-sm font-semibold text-gray-900">{title}</h4>

    {isLoading ? (
      <div className="flex h-32 items-center justify-center text-xs text-gray-400">
        Loading…
      </div>
    ) : data.length === 0 ? (
      <div className="flex h-32 items-center justify-center text-xs text-gray-400">
        No data for this period
      </div>
    ) : (
      <div className="flex items-center gap-4">
        <ResponsiveContainer width={140} height={140}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={38}
              outerRadius={62}
              paddingAngle={2}
              dataKey="value"
            >
              {data.map((entry, idx) => (
                <Cell
                  key={entry.name}
                  fill={PIE_COLORS[idx % PIE_COLORS.length]}
                />
              ))}
            </Pie>
            <Tooltip content={<PieChartTooltip />} />
          </PieChart>
        </ResponsiveContainer>

        <div className="flex-1 space-y-1.5">
          {data.map((item, idx) => (
            <div key={item.name} className="flex items-center gap-2">
              <div
                className="h-2 w-2 flex-shrink-0 rounded-full"
                style={{
                  backgroundColor: PIE_COLORS[idx % PIE_COLORS.length],
                }}
              />
              <span className="min-w-0 flex-1 truncate text-[10px] text-gray-600">
                {item.name}
              </span>
              <span className="text-[10px] font-semibold text-gray-500">
                {item.pct}%
              </span>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);
